import type {
  CounterpartyFirm,
  CounterpartyProfileResponse,
  CounterpartyRelation,
  PublishedAffiliation,
} from "@ondo/shared";
import { standsCurrent } from "@ondo/shared";

import { formatLongDay } from "../lib/dates.ts";
import { COUNTERPARTY_COPY } from "./counterparty.constants.ts";

/**
 * The profile, decided for the screen — OND-81.
 *
 * Pure. Splits the shared record into what is live and what is over, so the
 * view draws two lists and never an `if` about a standing; words every date;
 * and reduces the registry flag to one sentence. Nothing here filters: what
 * the server sent is what may be shown, by the database's own audience rules.
 */
export type CounterpartyView =
  | { kind: "closed" }
  | { kind: "loading"; ondoId: string }
  | { kind: "error"; ondoId: string; message: string }
  | { kind: "open"; ondoId: string; profile: ProfileLines };

export interface RelationLine {
  key: string;
  label: string;
  /** The work's title, or the relation's own word when it is not about a work. */
  subject: string;
  standing: string;
  when: string;
}

export interface ProfileLines {
  displayName: string;
  kindWord: string;
  confirmed: boolean;
  confirmedWord: string;
  since: string;
  firms: { key: string; name: string; seat: string; confirmed: boolean }[];
  current: RelationLine[];
  ended: RelationLine[];
  affiliations: PublishedAffiliation[];
}

export function toProfileLines(response: CounterpartyProfileResponse): ProfileLines {
  const { identity } = response;
  const lines = response.relations.map(toRelationLine);

  return {
    displayName: identity.displayName,
    kindWord:
      identity.kind === "organisation" ? COUNTERPARTY_COPY.organisation : COUNTERPARTY_COPY.person,
    confirmed: identity.verified,
    confirmedWord: identity.verified
      ? COUNTERPARTY_COPY.confirmed
      : COUNTERPARTY_COPY.notConfirmed,
    since: COUNTERPARTY_COPY.since(formatLongDay(identity.since)),
    firms: response.firms.map(toFirmLine),
    current: lines.filter((line) => standsCurrent(line.standingWord)).map(strip),
    ended: lines.filter((line) => !standsCurrent(line.standingWord)).map(strip),
    affiliations: response.affiliations,
  };
}

function toFirmLine(firm: CounterpartyFirm): ProfileLines["firms"][number] {
  return {
    key: firm.ondoId,
    name: firm.displayName,
    seat: COUNTERPARTY_COPY.seat(firm.title, firm.level),
    confirmed: firm.verified,
  };
}

function toRelationLine(
  relation: CounterpartyRelation,
  index: number,
): RelationLine & { standingWord: string } {
  return {
    key: `${relation.relation}-${relation.startedAt}-${String(index)}`,
    label: COUNTERPARTY_COPY.relation(relation.relation),
    subject: relation.workTitle ?? COUNTERPARTY_COPY.relation(relation.relation),
    standing: COUNTERPARTY_COPY.standing(relation.standing),
    standingWord: relation.standing,
    when: formatLongDay(relation.endedAt ?? relation.startedAt),
  };
}

function strip(line: RelationLine & { standingWord: string }): RelationLine {
  const { standingWord: _standingWord, ...rest } = line;
  return rest;
}
